import React, { useCallback } from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Pagination, CircularProgress } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { skillBiteSlice } from '../../../reducers'
import makeStyles from '@mui/styles/makeStyles'
import { ActionButton } from '../../../components'
import { useNavigate, useParams, useSearchParams, generatePath } from 'react-router-dom'
import { routes } from '../../../routes'
import { useTranslation } from '../../../hooks'
import { variant } from '../../../components/action-buttons/action-button/style'

export const useStyles = makeStyles(({ breakpoints, palette, spacing, typography }) => ({
  paginationUl: {
    '& li:first-child, li:last-child': {
      '& svg': {
        color: variant('primary', palette).backgroundColor
      }
    },
    '& li': {
      '& .Mui-selected': {
        ...variant('primary', palette)
      }
    }
  },
  button: {
    minWidth: 125,
    [breakpoints.up('sm')]: {
      minWidth: 275
    }
  }
}))

export const SkillBiteNavigation = ({ children, isLoading }) => {
  const { skillBiteId, sessionId } = useParams()
  const { t } = useTranslation('skill_bite')
  const classes = useStyles()
  const session = useSelector(skillBiteSlice.selectors.selectSession)
  const currentSection = useSelector(skillBiteSlice.selectors.selectCurrentSection)
  const currentPage = useSelector(skillBiteSlice.selectors.selectCurrentPage)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const isPreview = searchParams.has('preview')

  const [trackFinishSession] = useEvent([skillBiteSlice.eventBus.trackFinishSession({
    onSuccess: useCallback(() => {
      navigate(generatePath(routes['skill-bite'].path, {
        skillBiteId
      }))
    }, [navigate, skillBiteId])
  })])

  const loading = isLoading || trackFinishSession.isWorking

  const isFirstSection = currentSection && currentSection.id === session?.sections[0].id
  const isLastSection = currentSection && currentSection.id === session?.sections[session?.sections.length - 1].id
  const hasInnerPagination = currentSection?.pages.length > 1

  const currentPageIndex = currentSection?.pages.findIndex(page => page.id === currentPage?.id) + 1

  const isFirstSectionPage = currentPage && currentPage.id === currentSection?.pages[0].id
  const isLastSectionPage = currentPage && currentPage.id === currentSection?.pages[currentSection?.pages.length - 1].id
  const sessionHasEnded = isLastSection && isLastSectionPage

  const handleNextSection = useCallback(() => dispatch(skillBiteSlice.actions.nextSection()), [dispatch])
  const handlePreviousSection = useCallback(() => dispatch(skillBiteSlice.actions.previousSection()), [dispatch])

  const handleChangeInnerPagination = useCallback((event, page) => {
    dispatch(skillBiteSlice.actions.changeSectionPage({ pageIndex: page - 1 }))
  }, [dispatch])

  const handleEndSession = useCallback(() => {
    if (isPreview) {
      navigate(generatePath(routes['skill-bite'].path, {
        skillBiteId
      }))
      return
    }

    if (from === 'cypress') {
      navigate(routes.home.path)
    } else {
      trackFinishSession.trigger({
        skillBiteId,
        sessionId,
        from,
        reporting: true
      })
    }
  }, [skillBiteId, sessionId, trackFinishSession, navigate, from, isPreview])

  const shouldShowPreviousSectionButton = !isLoading && !isFirstSection && (!hasInnerPagination || isFirstSectionPage)
  const shouldShowNextSectionButton = !isLoading && !isLastSection && (!hasInnerPagination || isLastSectionPage)

  return (
    <Box>
      {children}
      {hasInnerPagination && (
        <Box display='flex' flexDirection='row' justifyContent='center' mt={6}>
          <Pagination
            count={currentSection?.pages.length}
            shape='rounded'
            color='primary'
            page={currentPageIndex}
            classes={{ ul: classes.paginationUl }}
            onChange={handleChangeInnerPagination}
            data-test='inner-pagination'
          />
        </Box>
      )}
      <Box display='flex' flexDirection='row' mt={8} mb={2}>
        {shouldShowPreviousSectionButton && (
          <ActionButton
            label={`< ${t('previous')}`}
            variant='secondary'
            onClick={handlePreviousSection}
            data-test='previous-section-button'
            className={classes.button}
            disabled={loading}
          />
        )}

        <Box display='flex' flex={1} />

        {shouldShowNextSectionButton && (
          <ActionButton
            label={`${t('next')} >`}
            variant='primary'
            onClick={handleNextSection}
            data-test='next-section-button'
            className={classes.button}
            disabled={loading}
          />
        )}

        {sessionHasEnded && (
          <ActionButton
            label={loading ? <CircularProgress size={16} /> : t('complete_session')}
            variant='primary'
            onClick={handleEndSession}
            data-test='complete-session-button'
            className={classes.button}
            disabled={loading}
          />
        )}
      </Box>
    </Box>
  )
}
